import { CSSProperties, useMemo, useState } from "react";
import { useCombobox, useMultipleSelection } from "downshift";

export type MultipleSelectComboboxProps<T> = {
  selectedItems: T[];
  addSelectedItem: (item: T) => void;
  removeSelectedItem: (item: T) => void;
  getFilteredItems: (selectedItems: T[], inputValue: string) => T[];
  itemIsSelected: (item: T) => boolean;
  itemToString: (item: T | null) => string;
  placeholder?: string;
  itemKey: keyof T;
  showSelectedItems?: boolean; // not in use, yet
  inputClassName?: string;
  inputStyle?: CSSProperties;
  menuClassName?: string;
  menuStyle?: CSSProperties;
  listItemClassName?: string;
  listItemStyle?: CSSProperties;
};

export default function MultipleSelectCombobox<T>(
  props: MultipleSelectComboboxProps<T>
) {
  const {
    addSelectedItem,
    getFilteredItems,
    itemIsSelected,
    itemToString,
    removeSelectedItem,
    selectedItems,
  } = props;
  const [inputValue, setInputValue] = useState("");

  const items = useMemo(
    () => getFilteredItems(selectedItems, inputValue),
    [selectedItems, inputValue, getFilteredItems]
  );
  const { getDropdownProps } = useMultipleSelection({
    selectedItems,
  });

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items,
    itemToString,
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    inputValue,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep the menu open after selection.
            highlightedIndex: state.highlightedIndex, // with the first option highlighted.
          };
        default:
          return { ...changes };
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            if (itemIsSelected(newSelectedItem)) {
              removeSelectedItem(newSelectedItem);
              // setSelectedItems(
              //   selectedItems.filter((item) => item.knr !== newSelectedItem.knr)
              // );
              // setInputValue("");
            } else {
              addSelectedItem(newSelectedItem);
              // setInputValue("");
            }
          }
          break;

        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue ?? "");
          break;
        default:
          break;
      }
    },
  });

  return (
    <div className="position-relative">
      <input
        placeholder={props.placeholder ?? "Søg og vælg ..."}
        className={`kommune-combobox-input w-100 ${isOpen ? "is-open" : ""} ${
          props.inputClassName
        }`}
        style={props.inputStyle}
        {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
      />
      <ul
        className={`kommune-combobox-ul  ${!isOpen && "hidden"} ${
          props.menuClassName
        }`}
        style={props.menuStyle}
        {...getMenuProps()}
      >
        {isOpen &&
          items.map((item, index) => (
            <li
              className={`filtermenuitem ${
                highlightedIndex === index ? "active " : ""
              } ${itemIsSelected(item) ? "bg-s" : ""} ${props.listItemStyle}`}
              key={`${item[props.itemKey]}${index}`}
              style={props.listItemStyle}
              {...getItemProps({ item, index })}
            >
              <input
                className="form-check-input mb-1 cursor-pointer"
                type="checkbox"
                value=""
                id={`${item[props.itemKey]}${index}`}
                readOnly
                checked={itemIsSelected(item)}
              />
              <span>{itemToString(item)}</span>
            </li>
          ))}
        {isOpen && items.length === 0 && (
          <li className={`filtermenuitem`}>
            <span>Intet resultat for "{inputValue}"</span>
          </li>
        )}
      </ul>
    </div>
  );
}
